import { Grib2JsonData, WindGeoJSON } from '../types';
import { detectGridParameters, roundToPrecision } from './gridDetector';

/**
 * Transforms a GeoJSON FeatureCollection of wind data points to grib2json format
 * required by leaflet-velocity
 *
 * @param geoJSON - GeoJSON FeatureCollection with Point geometries and wind_u/wind_v properties
 * @param timestamp - Optional timestamp (milliseconds) for the data. If not provided, extracted from first feature
 * @returns Array of 2 Grib2JsonData objects (U and V components)
 * @throws Error if data format is invalid or not on a regular grid
 */
export function transformGeoJSONToGrib2Json(
  geoJSON: WindGeoJSON,
  timestamp?: number
): Grib2JsonData[] {
  const features = geoJSON.features;

  if (!features || features.length === 0) {
    throw new Error('GeoJSON FeatureCollection is empty');
  }

  // Detect grid parameters
  const gridParams = detectGridParameters(geoJSON);

  // Validate grid size (prevent performance issues)
  const gridSize = gridParams.nx * gridParams.ny;
  const MAX_GRID_SIZE = 250000; // 500x500 maximum

  if (gridSize > MAX_GRID_SIZE) {
    throw new Error(
      `Grid size ${gridSize} (${gridParams.nx}×${gridParams.ny}) exceeds maximum ` +
      `allowed size of ${MAX_GRID_SIZE}. Consider reducing resolution or area.`
    );
  }

  // Create coordinate-to-feature map for fast lookup
  const featureMap = new Map<string, typeof features[0]['properties']>();

  features.forEach(feature => {
    const [lon, lat] = feature.geometry.coordinates;
    const key = `${roundToPrecision(lat, 4)},${roundToPrecision(lon, 4)}`;
    featureMap.set(key, feature.properties);
  });

  // Build sorted arrays in scanMode 0 order (west-to-east, north-to-south)
  const uData: number[] = [];
  const vData: number[] = [];
  let missingCount = 0;

  // Iterate through grid: north to south (decreasing latitude)
  for (let latIdx = 0; latIdx < gridParams.ny; latIdx++) {
    const lat = gridParams.la1 - (latIdx * gridParams.dy);

    // Iterate through grid: west to east (increasing longitude)
    for (let lonIdx = 0; lonIdx < gridParams.nx; lonIdx++) {
      const lon = gridParams.lo1 + (lonIdx * gridParams.dx);

      // Look up feature at this grid cell
      const key = `${roundToPrecision(lat, 4)},${roundToPrecision(lon, 4)}`;
      const properties = featureMap.get(key);

      if (properties) {
        // Validate wind components exist
        if (typeof properties.wind_u !== 'number' || typeof properties.wind_v !== 'number') {
          uData.push(0);
          vData.push(0);
        } else {
          uData.push(properties.wind_u);
          vData.push(properties.wind_v);
        }
      } else {
        // Missing data point - fill with zero
        uData.push(0);
        vData.push(0);
        missingCount++;
      }
    }
  }

  // Validate data length
  const expectedLength = gridParams.nx * gridParams.ny;
  if (uData.length !== expectedLength || vData.length !== expectedLength) {
    throw new Error(
      `Data length mismatch: expected ${expectedLength}, ` +
      `got U=${uData.length}, V=${vData.length}`
    );
  }

  // Extract or create reference time
  let refTime: string;
  if (timestamp) {
    refTime = new Date(timestamp).toISOString();
  } else {
    // Try to extract from first feature
    const firstFeature = features[0];
    if (firstFeature?.properties?.timestamp) {
      refTime = new Date(firstFeature.properties.timestamp).toISOString();
    } else {
      // Fallback to current time
      refTime = new Date().toISOString();
    }
  }

  // Build headers for U and V components
  const baseHeader = {
    parameterCategory: 2, // Meteorological - Momentum
    dx: gridParams.dx,
    dy: gridParams.dy,
    nx: gridParams.nx,
    ny: gridParams.ny,
    la1: gridParams.la1,
    lo1: gridParams.lo1,
    la2: gridParams.la2,
    lo2: gridParams.lo2,
    refTime: refTime,
    forecastTime: 0, // Instant/current time
  };

  return [
    {
      header: { ...baseHeader, parameterNumber: 2 }, // U-component (eastward)
      data: uData,
    },
    {
      header: { ...baseHeader, parameterNumber: 3 }, // V-component (northward)
      data: vData,
    },
  ];
}
